import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { getRelativeTimeString } from "@/lib/dates";
import { useDashboardOrgProject } from "@/lib/org-project-context/hooks";
import type { Project } from "../types";
import { DataTableRowActions } from "./data-table-row-actions";

const NameCell = ({ project }: { project: Project }) => {
  const { activeProjectId } = useDashboardOrgProject();
  const isActive = activeProjectId === project.project_id;

  return (
    <div className="flex items-center gap-2.5">
      <span className="font-medium">{project.name}</span>
      {!!isActive && <Badge>Active</Badge>}
    </div>
  );
};

const IdCell = ({ projectId }: { projectId: string }) => (
  <div className="flex items-center gap-2">
    <span className="font-mono text-muted-foreground text-sm">{projectId}</span>
    <CopyButton content={projectId} variant="ghost" />
  </div>
);

export const createProjectsColumns = (): ColumnDef<Project>[] => [
  {
    id: "id",
    accessorKey: "project_id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => <IdCell projectId={row.original.project_id} />,
    meta: {
      displayName: "ID",
      defaultVisible: false,
    },
  },
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => <NameCell project={row.original} />,
    meta: {
      displayName: "Name",
      defaultVisible: true,
    },
  },
  {
    id: "description",
    accessorKey: "description",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    cell: ({ row }) => {
      const description = row.original.description;
      if (!description) {
        return <span className="text-muted-foreground">—</span>;
      }

      return (
        <span className="line-clamp-1 max-w-lg overflow-hidden text-ellipsis whitespace-nowrap">
          {description}
        </span>
      );
    },
    meta: {
      displayName: "Description",
      defaultVisible: true,
    },
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => {
      const { created_at } = row.original;
      if (!created_at) {
        return <span className="text-muted-foreground">—</span>;
      }
      const date = new Date(created_at);
      return (
        <time
          className="whitespace-nowrap text-muted-foreground text-sm"
          dateTime={date.toISOString()}
          title={date.toUTCString()}
        >
          {getRelativeTimeString(date)}
        </time>
      );
    },
    meta: {
      displayName: "Created",
      defaultVisible: true,
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
    enableHiding: false,
  },
];

export const columns = createProjectsColumns();
